import { useState } from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import {
  dealQuery,
  formatMoney,
  useMoveDeal,
  useUpdateDeal,
  useDeleteDeal,
  useCreateTask,
  useCompleteTask,
  useCreateNote,
} from '../lib/crm.js';

const BAND_LABELS: Record<string, string> = {
  kalt: 'kalt',
  lauwarm: 'lauwarm',
  warm: 'warm',
  heiss: 'heiß',
};

export function DealDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery(dealQuery(id));

  const move = useMoveDeal();
  const update = useUpdateDeal(id);
  const remove = useDeleteDeal();
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const createNote = useCreateNote();

  const [note, setNote] = useState('');
  const [task, setTask] = useState('');
  const [lostReason, setLostReason] = useState('');

  if (isLoading) return <p className="muted">Wird geladen …</p>;

  if (isError) {
    return (
      <div className="alert" data-tone="danger" role="alert">
        {error instanceof Error ? error.message : 'Deal konnte nicht geladen werden.'}
      </div>
    );
  }
  if (!data) return null;

  const { deal, stages, contacts, tasks, notes } = data;
  const target = stages.find((s) => s.id === deal.stageId);

  async function handleDelete() {
    await remove.mutateAsync(id);
    await navigate({ to: '/crm' });
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/crm">CRM</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{deal.title}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{deal.title}</h1>
          <p className="muted text-sm">
            {formatMoney(deal.value, deal.currency)} · Stufe {deal.stageName} ·{' '}
            {target?.type === 'open'
              ? `${deal.probability} % Wahrscheinlichkeit`
              : deal.status === 'won'
                ? 'gewonnen'
                : 'verloren'}
            {deal.rotting && ' · liegengeblieben'}
          </p>
        </div>
        <Button variant="danger" onClick={handleDelete} loading={remove.isPending}>
          Löschen
        </Button>
      </header>

      <div className="detail-grid">
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Stufe</h2>
            <div className="field">
              <label className="field__label" htmlFor="stage">
                Aktuelle Stufe
              </label>
              <select
                id="stage"
                className="input"
                value={deal.stageId}
                onChange={(e) => move.mutate({ id, stageId: e.target.value, lostReason })}
              >
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </div>

            {/*
              Der Grund ist freiwillig, aber er ist die einzige Auswertung, die
              später sagen kann, WARUM Deals verloren gehen.
            */}
            <Field
              label="Verlustgrund (optional)"
              value={lostReason || (deal.lostReason ?? '')}
              onChange={(e) => setLostReason(e.target.value)}
              placeholder="z. B. Preis, Zeitpunkt, Wettbewerb"
            />
            {deal.status === 'lost' && deal.lostReason && (
              <p className="muted text-sm">Festgehalten: {deal.lostReason}</p>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Wert</h2>
            <ValueEditor
              value={deal.value}
              currency={deal.currency}
              busy={update.isPending}
              onSave={(value) => update.mutate({ value })}
            />
            <dl className="definition-grid">
              <div>
                <dt>In dieser Stufe seit</dt>
                <dd>{new Date(deal.stageEnteredAt).toLocaleDateString('de-CH')}</dd>
              </div>
              <div>
                <dt>Angelegt</dt>
                <dd>{new Date(deal.createdAt).toLocaleDateString('de-CH')}</dd>
              </div>
            </dl>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Beteiligte</h2>
          {contacts.length === 0 ? (
            <p className="muted text-sm">Noch niemand zugeordnet.</p>
          ) : (
            <ul className="people">
              {contacts.map((contact) => (
                <li key={contact.id}>
                  <Link to="/kontakte/$id" params={{ id: contact.id }}>
                    {[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                      contact.email}
                  </Link>
                  <span className="muted text-sm">{contact.email}</span>
                  <span className="badge" data-band={contact.band}>
                    {contact.leadScore} · {BAND_LABELS[contact.band] ?? contact.band}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <div className="detail-grid">
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Aufgaben</h2>
            {tasks.filter((t) => t.status === 'open').length === 0 && (
              <p className="muted text-sm">Nichts offen.</p>
            )}
            <ul className="tasks">
              {tasks
                .filter((t) => t.status === 'open')
                .map((t) => (
                  <li key={t.id} data-overdue={t.overdue ? 'true' : undefined}>
                    <div>
                      <strong>{t.title}</strong>
                      {t.dueAt && (
                        <span className="muted text-sm">
                          {' '}
                          — fällig {new Date(t.dueAt).toLocaleDateString('de-CH')}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => completeTask.mutate(t.id)}
                      loading={completeTask.isPending}
                    >
                      Erledigt
                    </Button>
                  </li>
                ))}
            </ul>

            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (task.trim().length === 0) return;
                createTask.mutate({ title: task.trim(), dealId: id });
                setTask('');
              }}
            >
              <input
                className="input"
                value={task}
                onChange={(e) => setTask(e.target.value)}
                placeholder="Neue Aufgabe"
                aria-label="Neue Aufgabe"
              />
              <Button type="submit" loading={createTask.isPending}>
                Hinzufügen
              </Button>
            </form>
          </div>
        </section>

        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Notizen</h2>
            {notes.length === 0 && <p className="muted text-sm">Noch keine Notiz.</p>}
            <ul className="notes">
              {notes.map((entry) => (
                <li key={entry.id}>
                  <p>{entry.body}</p>
                  <span className="muted text-sm">
                    {new Date(entry.createdAt).toLocaleString('de-CH')}
                  </span>
                </li>
              ))}
            </ul>

            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (note.trim().length === 0) return;
                createNote.mutate({ body: note.trim(), dealId: id });
                setNote('');
              }}
            >
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notiz festhalten"
                aria-label="Notiz"
              />
              <Button type="submit" loading={createNote.isPending}>
                Speichern
              </Button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function ValueEditor({
  value,
  currency,
  busy,
  onSave,
}: {
  value: number;
  currency: string;
  busy: boolean;
  onSave: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const changed = Number(draft) !== value && draft.trim().length > 0;

  return (
    <form
      className="inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (changed) onSave(Number(draft));
      }}
    >
      <input
        className="input"
        type="number"
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-label={`Wert in ${currency}`}
      />
      <Button type="submit" disabled={!changed} loading={busy}>
        Übernehmen
      </Button>
    </form>
  );
}
